import { z } from 'zod';
/**
 * Saved views on /transactions (uiv2 BE8, TX-09): a name and the page's
 * filters, per user per workspace. Declared once so the route, the page and
 * the tests agree.
 */
/** A view's name, in code points, and how many one user may keep per workspace. */
export const TRANSACTION_VIEW_NAME_MAX_LENGTH = 60;
export const TRANSACTION_VIEWS_MAX = 20;
/**
 * The dates a view can keep RELATIVE to the day it is opened. A range that is
 * none of these is kept as the fixed days it names.
 */
export const TRANSACTION_VIEW_DATE_PRESETS = ['this_month', 'last_month', 'last_30_days'];
/** The page's status control. */
export const TRANSACTION_VIEW_STATUSES = ['all', 'pending', 'approved', 'flagged'];
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const label = z.string().trim().min(1).max(200);
const amount = z.number().finite().nonnegative();
/**
 * The filters a view carries. A filter the view does not set is ABSENT — never
 * `null` — so "no category" cannot be told apart from "any category" by
 * accident. `strict()`: zod strips an unknown key by default, and a view saved
 * from a newer page with a filter this server does not know would otherwise be
 * stored without it and reported saved.
 */
export const transactionViewFiltersSchema = z
    .object({
    search: label.optional(),
    status: z.enum(TRANSACTION_VIEW_STATUSES).optional(),
    categoryId: z.string().uuid().optional(),
    accountType: label.optional(),
    type: z.enum(['credit', 'debit']).optional(),
    minAmount: amount.optional(),
    maxAmount: amount.optional(),
    sourceType: label.optional(),
    date: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('preset'), preset: z.enum(TRANSACTION_VIEW_DATE_PRESETS) }).strict(),
        z.object({ kind: z.literal('range'), from: isoDay, to: isoDay }).strict(),
    ])
        .optional(),
})
    .strict()
    .refine((f) => f.minAmount === undefined || f.maxAmount === undefined || f.minAmount <= f.maxAmount, 'amount range')
    .refine((f) => f.date?.kind !== 'range' || f.date.from <= f.date.to, 'date range');
/** `POST /transactions/saved-views` body. */
export const transactionSavedViewSchema = z
    .object({
    name: z
        .string()
        .transform((s) => s.replace(/\s+/g, ' ').trim())
        .refine((s) => s.length > 0, 'empty')
        .refine((s) => Array.from(s).length <= TRANSACTION_VIEW_NAME_MAX_LENGTH, 'too long'),
    filters: transactionViewFiltersSchema,
})
    .strict();
//# sourceMappingURL=transaction-views.js.map